import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getAccessToken, setAccessToken, clearAccessToken } from './tokenStore';

const backendUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/';

const isTokenExpiringSoon = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now() + 30000;
  } catch {
    return true;
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const refreshAccessToken = async () => {
  try {
    // Cookie carries the refresh token — no body needed
    const response = await axios.post(
      `${backendUrl}api/token/cookie-refresh/`,
      {},
      { withCredentials: true }
    );
    if (response.status === 200) {
      setAccessToken(response.data.access);
      return response.data.access;
    }
    return null;
  } catch {
    clearAccessToken();
    return null;
  }
};

const axiosInstance = axios.create({ baseURL: backendUrl });

axiosInstance.interceptors.request.use(
  async (config) => {
    if (
      config.url.includes('/api/token/') ||
      config.url.includes('/api/auth/register/') ||
      config.url.includes('/api/auth/verify-email/')
    ) {
      return config;
    }

    try {
      const token = getAccessToken();

      if (token && isTokenExpiringSoon(token)) {
        if (!isRefreshing) {
          isRefreshing = true;
          const newToken = await refreshAccessToken();
          isRefreshing = false;

          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            return config;
          } else {
            processQueue(new Error('Token refresh failed'), null);
            window.location.href = '/login';
            return Promise.reject(new Error('Token refresh failed'));
          }
        } else {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (t) => { config.headers.Authorization = `Bearer ${t}`; resolve(config); },
              reject,
            });
          });
        }
      } else if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } else {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
